import { FC } from 'react';
import { Navigate } from 'react-router-dom';
import { ViewLoader } from '~/components';
import { withErrorBoundary } from '~/hocs';
import { useCanCreateOrganization } from '~/hooks';
import { useIsSSO } from '~/hooks/useIsSSO';
import { useOrganizationsQuery, useWorkspacesQuery } from '~/queries';

/**
 * This is used as placeholder route to redirect user further down the path
 * 1. if SSO user doesn't have any organization -> send to placeholder page (lobby)
 * 2. if user doesn't have any organization -> send to organization creation
 * 3. if user doesn't have any workspaces -> send to organization root -> workspace creation
 * 4. if we don't have last visited workspace -> send to organization root -> first workspace from list
 * 5. if user has both -> send to last used workspace or first from the list
 * 6. if can't find last used workspace -> use first from the list
 */
export const UserRoot: FC = withErrorBoundary(() => {
  const { isSSO } = useIsSSO();

  const {
    data: organizations,
    isFetched: areOrganizationsFetched,
    isFetching: isFetchingOrganizations,
  } = useOrganizationsQuery();
  const { data: workspaces = [], isFetching: isFetchingWorkspaces } = useWorkspacesQuery();
  const canCreateNewOrganization = useCanCreateOrganization();

  if (!areOrganizationsFetched || isFetchingOrganizations || isFetchingWorkspaces) {
    return <ViewLoader key="initial" initial />;
  }

  if (isSSO && organizations?.length === 0) {
    return <Navigate to="/lobby" replace />;
  }

  if (!canCreateNewOrganization && organizations?.length === 0) {
    return <Navigate to="/join-organization" replace />;
  }

  if (organizations === undefined || organizations?.length === 0) {
    return <Navigate to="/create-organization" replace />;
  }

  const lastOrganizationShortName = window.localStorage.getItem('currentOrganizationShortName');
  const lastWorkspaceShortName = window.localStorage.getItem('currentWorkspaceShortName');

  // Agent workspaces are not navigable on this platform (the side navigation
  // filters them out the same way), so never select one as the destination.
  const visibleWorkspaces = workspaces.filter((workspace) => workspace.is_agent_workspace !== true);

  const lastUsedOrganizationWorkspaces = (
    lastOrganizationShortName ? visibleWorkspaces : []
  ).filter(
    (workspace) =>
      workspace.org_short_name === lastOrganizationShortName && workspace.permissions?.length,
  );

  const lastUsedWorkspace =
    lastUsedOrganizationWorkspaces.find(
      (workspace) => workspace.short_name === lastWorkspaceShortName,
    ) ?? lastUsedOrganizationWorkspaces[0];

  if (!lastUsedWorkspace || !lastWorkspaceShortName) {
    const lastUsedOrganization =
      organizations.find((organization) => organization.short_name === lastOrganizationShortName) ??
      organizations[0];

    const firstWorkspaceToRedirectTo = visibleWorkspaces.find(
      ({ organization_id: organizationId, permissions }) =>
        organizationId === lastUsedOrganization.id && permissions?.length,
    );

    if (!firstWorkspaceToRedirectTo) {
      return <Navigate to={`/${lastUsedOrganization.short_name}`} replace />;
    }

    return (
      <Navigate
        to={`/${lastUsedOrganization.short_name}/${firstWorkspaceToRedirectTo.short_name}`}
        replace
      />
    );
  }

  return (
    <Navigate to={`/${lastUsedWorkspace.org_short_name}/${lastUsedWorkspace.short_name}`} replace />
  );
});

export default UserRoot;
